import { useState } from "react";
import { useTranslation } from "@chr/online-shared-ui-localizations";
import { MultipleTextInput } from "../multiple-text-input/MultipleTextInput";
import { isEmail } from "../../utilities/validator";

export interface MultipleEmailInputProps {
  label: string;
  emails: string [];
  onChange: (values:string[]) => void;
  fullWidth?: boolean
}

export const MultipleEmailInput =({
  label,
  emails,
  onChange,
  fullWidth
}: MultipleEmailInputProps) => {
  const translate = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState('');
  const [touched, setTouched] = useState(false);

  const reachedMax = emails.length > 9 && translate('Maximum 10 emails reached.');

  const resetInput = () => {
    setInputValue('');
    setError('');
    setTouched(false);
  }

  const handleChange = (event:React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = event.target.value;

    // if user pastes a value into the input check for commas or semicolons
    const commaSeperated = value.split(',');
    const semicolonseperated = value.split(';');
    if(commaSeperated.length > 1){
      addMultipleEmails(commaSeperated, ', ');
    } else if(semicolonseperated.length > 1){
      addMultipleEmails(semicolonseperated, '; ');
    } else {
      setInputValue(value);
      validate(value, emails.length);
    }
  }

  const addMultipleEmails = (values:string[], seperator:string) => {
    const validEmails:string[] = [];
    const invalidEmails:string[] = [];

    values.forEach(value => {
      const input = value.trim();
      const isUnderMax = emails.length + validEmails.length < 10;
      const isDuplicate = emails.includes(input) || validEmails.includes(input);
      
      if(input === '') {
        return;
      } else if(isDuplicate) {
        invalidEmails.push(input);
      } else if(isUnderMax && validate(input, 1)){
        validEmails.push(input);
      } else {
        invalidEmails.push(input);
      }
    });

    onChange([...emails, ...validEmails]);
    if(invalidEmails.length > 0){
      const remainingInput = invalidEmails.join(seperator);
      setInputValue(remainingInput);
      if(emails.includes(remainingInput.trim()) || validEmails.includes(remainingInput.trim())) {
        setError(translate('Email already added.'));
      } else {
        validate(remainingInput, 1);
      }
      setTouched(true);
    } else {
      resetInput();
    }
  }

  const addEmail = (value:string) => {
    // Check for commas or semicolons seperated values
    const commaSeperated = value.split(',');
    const semicolonseperated = value.split(';');
    if(commaSeperated.length > 1){
      addMultipleEmails(commaSeperated, ', ');
    } else if(semicolonseperated.length > 1){
      addMultipleEmails(semicolonseperated, '; ');
    } else if(emails.includes(value)){
      setError(translate('Email already added.'));
      setTouched(true);
    } else if(validate(value, 1)){
      onChange([...emails, value]);
      resetInput();
    } else {
      // Ensure error displays
      setTouched(true);
    }
  };

  const removeEmail = (value:string) => {
      const newValues = emails?.filter(v => v !== value);
      onChange(newValues);
      validate('', newValues.length);
  }

  const validate = (newValue: string, emailsCount: number):boolean => {
    if(!newValue && emailsCount < 1){
      setError(translate('Field is required.'));
      return false;

    } else if(newValue && !isEmail(newValue)){
      setError(translate('Please enter a valid email address.'));
      return false;

    } else {
      setError('');
      return true;
    }
  }

  return (
    <MultipleTextInput
      label={label}
      error={Boolean(!reachedMax && touched && error)}
      helperText={reachedMax || (touched ? error : '')}
      disabled={!!reachedMax}
      required
      value={inputValue}
      values={emails}
      onRemoveValue={removeEmail}
      onAddValue={addEmail}
      onChange={handleChange}
      onBlur={(e) => {
        setTouched(true);
        validate(e.target.value, emails.length);
      }}
      fullWidth={fullWidth}
    />
  );
}
